export type AccountSettingsLocale = "en-US" | "ja-JP" | "ko-KR";

const STRINGS: Record<
  AccountSettingsLocale,
  {
    generalSection: string;
    passwordTitle: string;
    passwordDescription: string;
    emailVerificationTitle: string;
    emailVerificationVerifiedDescription: string;
    emailVerificationUnverifiedDescription: string;
    otpLoginTitle: string;
    otpLoginDescription: string;
    advancedSection: string;
    deactivateTitle: string;
    deactivateDescription: string;
    deleteTitle: string;
    deleteDescription: string;
    verifyEmailModalTitle: string;
    modalCancel: string;
    modalDone: string;
    authorizationSection: string;
    authorizationGoogleTitle: string;
    authorizationGoogleConnectedDescription: string;
    authorizationGoogleDisconnectedDescription: string;
    authorizationConnectButton: string;
    authorizationDisconnectButton: string;
    authorizationStatusConnected: string;
    authorizationStatusNotConnected: string;
    googleConnectModalTitle: string;
    googleConnectModalDescription: string;
    googleConnectPasswordLabel: string;
    googleConnectButtonLabel: string;
    googleConnectSuccess: string;
    googleConnectError: string;
    googleDisconnectSetupTitle: string;
    googleDisconnectSetupDescription: string;
    googleDisconnectSetupButton: string;
    googleDisconnectSetupSentTitle: string;
    googleDisconnectSetupSentDescription: string;
    googleDisconnectConfirmTitle: string;
    googleDisconnectConfirmDescription: string;
    googleDisconnectPasswordLabel: string;
    googleDisconnectSaveButton: string;
    googleDisconnectSuccess: string;
    googleDisconnectError: string;
    sessionsSection: string;
    sessionsDescription: string;
    sessionsEmpty: string;
    sessionsCurrentBadge: string;
    sessionsLastActivePrefix: string;
    sessionsSignOutAction: string;
    sessionsSignOutOtherAction: string;
    sessionsUnknownLocation: string;
    sessionsUnknownDevice: string;
    sessionsRevokeAllTitle: string;
    sessionsRevokeAllDescription: string;
    sessionsRevokeAllPasswordLabel: string;
    sessionsRevokeAllConfirm: string;
    sessionsRevokeAllSuccess: string;
    sessionsLoadError: string;
    sessionsRevokeError: string;
    sessionsRevokeAllError: string;
    passwordRequiredError: string;
  }
> = {
  "en-US": {
    generalSection: "General",
    passwordTitle: "Password",
    passwordDescription: "Update your account password.",
    emailVerificationTitle: "Email verification",
    emailVerificationVerifiedDescription: "Your email is verified.",
    emailVerificationUnverifiedDescription: "Verify your email to activate your signup award.",
    otpLoginTitle: "OTP login",
    otpLoginDescription: "Enable signing in with a one-time passcode.",
    advancedSection: "Advanced",
    deactivateTitle: "Deactivate account",
    deactivateDescription: "Hides your profile from view but leaves your content associated with your username.",
    deleteTitle: "Delete account",
    deleteDescription: "Permanently removes your profile and disassociates your username from your content.",
    verifyEmailModalTitle: "Verify your email",
    modalCancel: "Cancel",
    modalDone: "Done",
    authorizationSection: "Account authorization",
    authorizationGoogleTitle: "Google",
    authorizationGoogleConnectedDescription: "Google is connected to your Societea account.",
    authorizationGoogleDisconnectedDescription: "Connect Google so you can sign in with either your password or Google.",
    authorizationConnectButton: "Connect",
    authorizationDisconnectButton: "Disconnect",
    authorizationStatusConnected: "Connected",
    authorizationStatusNotConnected: "Not connected",
    googleConnectModalTitle: "Connect with Google",
    googleConnectModalDescription: "To continue, confirm your current password, then complete Google authorization.",
    googleConnectPasswordLabel: "Current password",
    googleConnectButtonLabel: "Continue with Google",
    googleConnectSuccess: "Google account connected successfully.",
    googleConnectError: "Failed to connect Google account.",
    googleDisconnectSetupTitle: "Disconnect Google account",
    googleDisconnectSetupDescription: "To disconnect your Google account, you first need to create a password for local sign-in. We'll email you a secure link to create it.",
    googleDisconnectSetupButton: "Continue",
    googleDisconnectSetupSentTitle: "Check your email",
    googleDisconnectSetupSentDescription: "We sent you a secure link to create your password. After you create it, sign in again with your email and password, then return here to finish disconnecting Google.",
    googleDisconnectConfirmTitle: "Disconnect Google account",
    googleDisconnectConfirmDescription: "To continue, confirm your password. This will disconnect Google and keep password sign-in available.",
    googleDisconnectPasswordLabel: "Password",
    googleDisconnectSaveButton: "Save",
    googleDisconnectSuccess: "Google account disconnected successfully.",
    googleDisconnectError: "Failed to disconnect Google account.",
    sessionsSection: "Active sessions",
    sessionsDescription: "Review the devices that currently have access to your account.",
    sessionsEmpty: "You only have the current session right now.",
    sessionsCurrentBadge: "Current",
    sessionsLastActivePrefix: "Last active",
    sessionsSignOutAction: "Sign out",
    sessionsSignOutOtherAction: "Sign out all other sessions",
    sessionsUnknownLocation: "Unknown location",
    sessionsUnknownDevice: "Unknown device",
    sessionsRevokeAllTitle: "Sign out all other sessions",
    sessionsRevokeAllDescription: "Enter your password to sign out every other active session on this account.",
    sessionsRevokeAllPasswordLabel: "Password",
    sessionsRevokeAllConfirm: "Sign out others",
    sessionsRevokeAllSuccess: "Other sessions were signed out successfully.",
    sessionsLoadError: "Failed to load your active sessions.",
    sessionsRevokeError: "Failed to sign out that session.",
    sessionsRevokeAllError: "Failed to sign out the other sessions.",
    passwordRequiredError: "Enter your password to continue.",
  },
  "ja-JP": {
    generalSection: "一般",
    passwordTitle: "パスワード",
    passwordDescription: "アカウントのパスワードを更新します。",
    emailVerificationTitle: "メール確認",
    emailVerificationVerifiedDescription: "メール確認が完了しています。",
    emailVerificationUnverifiedDescription: "サインアップ時のアワードを有効にするにはメールを確認してください。",
    otpLoginTitle: "OTPログイン",
    otpLoginDescription: "ワンタイムパスコードでサインインできるようにします。",
    advancedSection: "詳細設定",
    deactivateTitle: "アカウントを停止",
    deactivateDescription: "プロフィールを非表示にしますが、コンテンツはユーザー名に紐づいたまま残ります。",
    deleteTitle: "アカウントを削除",
    deleteDescription: "プロフィールを完全に削除し、コンテンツからユーザー名の紐づけを解除します。",
    verifyEmailModalTitle: "メールを確認",
    modalCancel: "キャンセル",
    modalDone: "完了",
    authorizationSection: "アカウント認証",
    authorizationGoogleTitle: "Google",
    authorizationGoogleConnectedDescription: "Google が Societea アカウントに接続されています。",
    authorizationGoogleDisconnectedDescription: "Google を接続すると、パスワードまたは Google のどちらでもサインインできます。",
    authorizationConnectButton: "接続",
    authorizationDisconnectButton: "接続解除",
    authorizationStatusConnected: "接続済み",
    authorizationStatusNotConnected: "未接続",
    googleConnectModalTitle: "Google と接続",
    googleConnectModalDescription: "続行するには、現在のパスワードを確認してから Google 認証を完了してください。",
    googleConnectPasswordLabel: "現在のパスワード",
    googleConnectButtonLabel: "Google で続行",
    googleConnectSuccess: "Google アカウントを接続しました。",
    googleConnectError: "Google アカウントを接続できませんでした。",
    googleDisconnectSetupTitle: "Google アカウントの接続解除",
    googleDisconnectSetupDescription: "Google アカウントの接続を解除するには、先にローカルサインイン用のパスワードを作成する必要があります。安全な作成リンクをメールでお送りします。",
    googleDisconnectSetupButton: "続ける",
    googleDisconnectSetupSentTitle: "メールを確認してください",
    googleDisconnectSetupSentDescription: "パスワード作成用の安全なリンクをメールで送りました。パスワード作成後、メールアドレスとパスワードで再度サインインしてから、ここに戻って Google の接続解除を完了してください。",
    googleDisconnectConfirmTitle: "Google アカウントの接続解除",
    googleDisconnectConfirmDescription: "続行するにはパスワードを確認してください。Google の接続を解除し、パスワードでのサインインは引き続き利用できます。",
    googleDisconnectPasswordLabel: "パスワード",
    googleDisconnectSaveButton: "保存",
    googleDisconnectSuccess: "Google アカウントの接続を解除しました。",
    googleDisconnectError: "Google アカウントの接続を解除できませんでした。",
    sessionsSection: "アクティブなセッション",
    sessionsDescription: "現在このアカウントにアクセスできるデバイスを確認できます。",
    sessionsEmpty: "現在はこのセッションだけが有効です。",
    sessionsCurrentBadge: "現在",
    sessionsLastActivePrefix: "最終アクティブ",
    sessionsSignOutAction: "サインアウト",
    sessionsSignOutOtherAction: "他のすべてのセッションをサインアウト",
    sessionsUnknownLocation: "不明な場所",
    sessionsUnknownDevice: "不明なデバイス",
    sessionsRevokeAllTitle: "他のすべてのセッションをサインアウト",
    sessionsRevokeAllDescription: "このアカウントの他のアクティブなセッションをすべて終了するには、パスワードを入力してください。",
    sessionsRevokeAllPasswordLabel: "パスワード",
    sessionsRevokeAllConfirm: "他をサインアウト",
    sessionsRevokeAllSuccess: "他のセッションをサインアウトしました。",
    sessionsLoadError: "アクティブなセッションを読み込めませんでした。",
    sessionsRevokeError: "このセッションをサインアウトできませんでした。",
    sessionsRevokeAllError: "他のセッションをサインアウトできませんでした。",
    passwordRequiredError: "続行するにはパスワードを入力してください。",
  },
  "ko-KR": {
    generalSection: "일반",
    passwordTitle: "비밀번호",
    passwordDescription: "계정 비밀번호를 업데이트합니다.",
    emailVerificationTitle: "이메일 인증",
    emailVerificationVerifiedDescription: "이메일 인증이 완료되었습니다.",
    emailVerificationUnverifiedDescription: "가입 어워드를 받으려면 이메일을 인증하세요.",
    otpLoginTitle: "OTP 로그인",
    otpLoginDescription: "일회용 패스코드로 로그인할 수 있게 합니다.",
    advancedSection: "고급",
    deactivateTitle: "계정 비활성화",
    deactivateDescription: "프로필은 숨기지만 콘텐츠는 사용자 이름과 연결된 상태로 유지합니다.",
    deleteTitle: "계정 삭제",
    deleteDescription: "프로필을 영구적으로 제거하고 콘텐츠에서 사용자 이름 연결을 해제합니다.",
    verifyEmailModalTitle: "이메일 인증",
    modalCancel: "취소",
    modalDone: "완료",
    authorizationSection: "계정 인증",
    authorizationGoogleTitle: "Google",
    authorizationGoogleConnectedDescription: "Google 이 Societea 계정에 연결되어 있습니다.",
    authorizationGoogleDisconnectedDescription: "Google 을 연결하면 비밀번호 또는 Google 중 어느 방식으로도 로그인할 수 있습니다.",
    authorizationConnectButton: "연결",
    authorizationDisconnectButton: "연결 해제",
    authorizationStatusConnected: "연결됨",
    authorizationStatusNotConnected: "연결되지 않음",
    googleConnectModalTitle: "Google 과 연결",
    googleConnectModalDescription: "계속하려면 현재 비밀번호를 확인한 다음 Google 인증을 완료하세요.",
    googleConnectPasswordLabel: "현재 비밀번호",
    googleConnectButtonLabel: "Google 로 계속",
    googleConnectSuccess: "Google 계정이 연결되었습니다.",
    googleConnectError: "Google 계정을 연결하지 못했습니다.",
    googleDisconnectSetupTitle: "Google 계정 연결 해제",
    googleDisconnectSetupDescription: "Google 계정을 연결 해제하려면 먼저 로컬 로그인용 비밀번호를 만들어야 합니다. 비밀번호를 만들 수 있는 안전한 링크를 이메일로 보내드립니다.",
    googleDisconnectSetupButton: "계속",
    googleDisconnectSetupSentTitle: "이메일을 확인하세요",
    googleDisconnectSetupSentDescription: "비밀번호를 만들 수 있는 안전한 링크를 이메일로 보냈습니다. 비밀번호를 만든 뒤 이메일과 비밀번호로 다시 로그인한 다음 여기로 돌아와 Google 연결 해제를 완료하세요.",
    googleDisconnectConfirmTitle: "Google 계정 연결 해제",
    googleDisconnectConfirmDescription: "계속하려면 비밀번호를 확인하세요. Google 연결은 해제되지만 비밀번호 로그인은 계속 사용할 수 있습니다.",
    googleDisconnectPasswordLabel: "비밀번호",
    googleDisconnectSaveButton: "저장",
    googleDisconnectSuccess: "Google 계정 연결이 해제되었습니다.",
    googleDisconnectError: "Google 계정 연결을 해제하지 못했습니다.",
    sessionsSection: "활성 세션",
    sessionsDescription: "현재 계정에 접근할 수 있는 기기를 검토하세요.",
    sessionsEmpty: "지금은 현재 세션만 활성 상태입니다.",
    sessionsCurrentBadge: "현재",
    sessionsLastActivePrefix: "마지막 활동",
    sessionsSignOutAction: "로그아웃",
    sessionsSignOutOtherAction: "다른 모든 세션 로그아웃",
    sessionsUnknownLocation: "알 수 없는 위치",
    sessionsUnknownDevice: "알 수 없는 기기",
    sessionsRevokeAllTitle: "다른 모든 세션 로그아웃",
    sessionsRevokeAllDescription: "이 계정의 다른 활성 세션을 모두 종료하려면 비밀번호를 입력하세요.",
    sessionsRevokeAllPasswordLabel: "비밀번호",
    sessionsRevokeAllConfirm: "다른 세션 로그아웃",
    sessionsRevokeAllSuccess: "다른 세션이 로그아웃되었습니다.",
    sessionsLoadError: "활성 세션을 불러오지 못했습니다.",
    sessionsRevokeError: "해당 세션을 로그아웃하지 못했습니다.",
    sessionsRevokeAllError: "다른 세션을 로그아웃하지 못했습니다.",
    passwordRequiredError: "계속하려면 비밀번호를 입력하세요.",
  },
};

export function resolveAccountSettingsLocale(): AccountSettingsLocale {
  if (typeof navigator === "undefined" || !navigator.language) {
    return "en-US";
  }
  const lang = navigator.language.toLowerCase();
  if (lang.startsWith("ja")) {
    return "ja-JP";
  }
  if (lang.startsWith("ko")) {
    return "ko-KR";
  }
  return "en-US";
}

export function getAccountSettingsUiStrings(
  locale?: AccountSettingsLocale
): (typeof STRINGS)["en-US"] {
  const key = locale ?? resolveAccountSettingsLocale();
  return STRINGS[key] ?? STRINGS["en-US"];
}
